import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Input, InputNumber, Modal, Row, Select, Space } from "antd";
import { Rule } from "antd/es/form";
import { CombustionFuelVolume, InpputByEnum, TagPI, dropdownOption, InputByEnum, Masters } from "src/entity/fuel-volume/fuel-volume.entity";
import { DeleteOutlined, PlusCircleOutlined } from "@ant-design/icons";
import ModalTitle from "src/components/AntdCustomPart/ModalTitle/ModalTitle";
import FormLabel from "src/components/AntdCustomPart/FormLabel/FormLabel";
import { onKeyDownNumberWithDecimal } from "src/utils/key-down-validate";
import CustomFormDatePicker from "src/components/AntdCustomPart/CustomFormDatePicker/CustomFormDatePicker";
import { formatNumber } from "src/utils/process-number";
import { FuelTypeEnum } from "src/entity/combustion/combustion.constants";

const inputVolumeOptions: dropdownOption[] = [
  { value: InputByEnum.PI, label: "PI" },
  { value: InputByEnum.MANUAL, label: "Manual" },
];
const inputDensityOptions: dropdownOption[] = [
  { value: InputByEnum.PI, label: "PI" },
  { value: InputByEnum.MANUAL, label: "Manual" },
];

const unitVolumeTagPI: dropdownOption[] = [
  {
    key: "kgHr",
    label: "Kg/hr",
    value: "Kg/hr",
  },
  {
    key: "tonHr",
    label: "Ton/hr",
    value: "Ton/hr",
  },
  {
    key: "m3hr",
    label: "M3/hr",
    value: "M3/hr",
  },
];

const FuelVolumeModal = (props: {
  open: boolean;
  initialData: CombustionFuelVolume;
  unitMaster: dropdownOption[];
  loading: boolean;
  onCancelFuelVolume: () => void;
  onSaveFuelVolume: (data: CombustionFuelVolume, combustionId: string) => void;
}) => {
  const { open, initialData, unitMaster, loading, onCancelFuelVolume, onSaveFuelVolume } = props;
  const [form] = Form.useForm();

  // const [unitVolumeOptions, setUnitVolumeOptions] = useState<dropdownOption[]>();

  const [unitDensityTagPI, setUnitDensityTagPI] = useState<dropdownOption[]>();
  const [isManualDisabled, setIsManualDisabled] = useState<boolean>(false);
  const [isM3hrSelected, setIsM3hrSelected] = useState<boolean>(false);
  const [combustionData, setCombustionData] = useState<CombustionFuelVolume>(initialData);
  const combustionId = initialData._id;

  const [unitVolumeManual, setUnitVolumeManual] = useState<dropdownOption[]>();

  const msgErrorInputPI = "field is required when Volume InputBy set PI";

  const validateRequired: Rule[] = [{ required: true }];

  const validateInputBy: Rule[] = [
    ({ getFieldValue }) => ({
      validator(_, value) {
        if (getFieldValue(["volume", "inputBy"]) === InpputByEnum.PI && !value) {
          if (isDensityTagPIOptional()) return Promise.resolve();
          return Promise.reject(new Error(msgErrorInputPI));
        }
        return Promise.resolve();
      },
    }),
  ];

  const validateVolumeTagPI: Rule[] = [
    ({ getFieldValue }) => ({
      validator(_, value) {
        if (getFieldValue(["volume", "inputBy"]) === InpputByEnum.PI && !value) {
          return Promise.reject(new Error(msgErrorInputPI));
        }
        if (value) {
          const dupTagPI = combustionData.volume.tagPI.filter((item) => item.tagPI == value);
          if (dupTagPI && dupTagPI.length > 1) {
            return Promise.reject(new Error("Duplicate Tag PI"));
          } else {
            return Promise.resolve();
          }
        }

        return Promise.resolve();
      },
    }),
  ];

  const validateInputByUnit: Rule[] = [
    ({ getFieldValue }) => ({
      validator(_, value) {
        if (getFieldValue(["volume", "inputBy"]) === InpputByEnum.PI && !value) {
          if (isDensityTagPIOptional()) return Promise.resolve();
          return Promise.reject(new Error(``));
        }
        return Promise.resolve();
      },
    }),
  ];

  const isDensityTagPIOptional = () => {
    const hasM3hr = combustionData.volume.tagPI?.some((el) => el?.unit === "M3/hr");
    const isNGorFG = [FuelTypeEnum.FG, FuelTypeEnum.NG].includes(combustionData.fuelType as FuelTypeEnum);
    return Boolean(hasM3hr && isNGorFG);
  };

  const validateDensityTagPI: Rule[] = [
    ({ getFieldValue }) => ({
      validator(_, value) {
        if (getFieldValue(["volume", "inputBy"]) !== InpputByEnum.PI) return Promise.resolve();
        if (isDensityTagPIOptional()) return Promise.resolve();
        if (!value) return Promise.reject(new Error(msgErrorInputPI));
        return Promise.resolve();
      },
    }),
  ];

  const validateDensityTagPIUnit: Rule[] = [
    ({ getFieldValue }) => ({
      validator(_, value) {
        if (getFieldValue(["volume", "inputBy"]) !== InpputByEnum.PI) return Promise.resolve();
        if (isDensityTagPIOptional()) return Promise.resolve();
        if (!value) return Promise.reject(new Error(msgErrorInputPI));
        return Promise.resolve();
      },
    }),
  ];

  const unitVolumeOptions = useMemo(() => {
    if (combustionData.volume.inputBy == InpputByEnum.PI) {
      if ([FuelTypeEnum.FG, FuelTypeEnum.NG].includes(combustionData.fuelType as FuelTypeEnum)) {
        return unitVolumeTagPI;
      } else {
        return unitVolumeTagPI.filter((el) => el.key !== "m3hr");
      }
    } else {
      return unitVolumeManual;
    }
  }, [combustionData.volume.inputBy, combustionData.fuelType]);

  const handleMapMaster = () => {
    let masterDensityTagPI = Masters.find((el) => el.label === "unitDensity");
    let optionsUnitDensityPI = masterDensityTagPI?.options.filter((el) => el.key === combustionData.fuelType);
    setUnitDensityTagPI(optionsUnitDensityPI);

    let optionUnitVolumePI = Masters.find((el) => el.label === "unitVolume");
    setUnitVolumeManual(optionUnitVolumePI?.options.filter((el) => el.key === combustionData.fuelType));
  };

  const handleChangeVolume = (field: keyof TagPI, value: string, index: number) => {
    if (field === "unit" && value === "M3/hr") {
      setIsM3hrSelected(true);
      // set density & unit to empty (use undefined so InputNumber shows empty, not 0)
      setCombustionData((prevState) => {
        return {
          ...prevState,
          density: {
            ...prevState.density,
            tagPI: {
              ...prevState.density.tagPI,
              tagPI: undefined as any,
              unit: undefined as any,
            },
          },
        };
      });
      form.setFieldsValue({
        density: {
          ...form.getFieldValue("density"),
          tagPI: { tagPI: undefined, unit: undefined },
        },
      });
    } else {
      setIsM3hrSelected(false);
    }
    setCombustionData((prevState) => {
      return {
        ...prevState,
        volume: {
          ...prevState.volume,
          tagPI: [...prevState.volume.tagPI].map((item, i) => (i === index ? { ...item, [field]: value } : item)),
        },
      };
    });
  };

  const handleChangeDensity = (field: keyof TagPI, value: string) => {
    setCombustionData((prevState) => {
      return {
        ...prevState,
        density: {
          ...prevState.density,
          tagPI: {
            ...prevState.density.tagPI,
            [field]: value,
          },
        },
      };
    });
  };

  const handleChangeInputBy = (field: "density" | "volume", value: string) => {
    if (field == "volume" && value === InpputByEnum.manual) {
      // setUnitVolumeOptions(volumeOptions);
      setIsManualDisabled(true);
      setCombustionData({
        ...combustionData,
        volume: {
          inputBy: value,
          tagPI: [{ tagPI: "", unit: "" }],
        },
        density: {
          inputBy: "",
          tagPI: { tagPI: "", unit: "" },
        },
        exceptionDate: [],
        exceptionDateArr: [],
      });
    } else {
      if (value === InpputByEnum.manual) {
        setCombustionData((prevState) => {
          return {
            ...prevState,
            density: {
              ...prevState.density,
              tagPI: {
                ...prevState.density.tagPI,
                tagPI: "",
              },
            },
          };
        });
      }
      // setUnitVolumeOptions(volumeOptions);
      setCombustionData((prevState) => {
        return {
          ...prevState,
          [field]: {
            ...prevState[field],
            inputBy: value,
          },
        };
      });
      setIsManualDisabled(false);
    }
  };

  const handleDeleteVolume = (index: number) => {
    if (combustionData.volume.tagPI.length > 1) {
      const formData = form.getFieldsValue();
      setCombustionData((prevState) => {
        return {
          ...formData,
          volume: {
            ...prevState.volume,
            tagPI: [...prevState.volume.tagPI].slice(0, index).concat([...prevState.volume.tagPI].slice(index + 1)),
          },
        };
      });
    }
  };

  const handleSaveCombustion = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      const tmpCombustion = combustionData;
      tmpCombustion.exceptionDateArr = values.exceptionDateArr;
      if (combustionId) {
        await onSaveFuelVolume(tmpCombustion, combustionId);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const addVolumeTagPI = () => {
    const newTag = { tagPI: "", unit: "" };
    const formData = form.getFieldsValue();
    setCombustionData((prevState) => ({
      ...formData,
      volume: {
        ...prevState.volume,
        tagPI: [...prevState.volume.tagPI, newTag],
      },
    }));
  };

  const checkHasM3hr = (data: any[]) => {
    const hasM3hr = data?.some((el: any) => el?.unit === "M3/hr");
    if (hasM3hr) {
      setIsM3hrSelected(true);
    } else {
      setIsM3hrSelected(false);
    }
  };

  const handleChangeForm = (changedValues: any, allValues: any) => {
    if (changedValues.exceptionDateArr) {
      const changeExceptionDate = changedValues.exceptionDateArr;
      const newExceptions: string[] = Array.from(new Set<string>(changeExceptionDate));
      setCombustionData((prevState) => ({
        ...prevState,
        exceptionDateArr: newExceptions,
      }));
    }

    if ([FuelTypeEnum.FG, FuelTypeEnum.NG].includes(combustionData.fuelType as FuelTypeEnum)) {
      const data = allValues?.volume?.tagPI;
      checkHasM3hr(data);
    }
  };

  useEffect(() => {
    handleMapMaster();
    if (combustionData.volume.inputBy === InpputByEnum.manual) {
      setIsManualDisabled(true);
    }
    if ([FuelTypeEnum.FG, FuelTypeEnum.NG].includes(combustionData.fuelType as FuelTypeEnum)) {
      checkHasM3hr(combustionData.volume.tagPI ?? []);
    }
    form.setFieldsValue(combustionData);
  }, []);

  useEffect(() => {
    form.setFieldsValue(combustionData);
    if (isDensityTagPIOptional() && (combustionData.density?.tagPI?.tagPI === "" || combustionData.density?.tagPI?.tagPI === "0")) {
      form.setFieldsValue({
        density: {
          ...form.getFieldValue("density"),
          tagPI: { tagPI: undefined, unit: undefined },
        },
      });
    }
  }, [combustionData]);

  useEffect(() => {
    if (combustionData.volume.inputBy === InpputByEnum.manual) {
      const optionUnitVolumePI = Masters.find((el) => el.label === "unitVolume");
      const resultUnit = optionUnitVolumePI?.options.find((el) => el.key === combustionData.fuelType);
      if (resultUnit) {
        const unit = resultUnit.value;
        if (unit) {
          setCombustionData((prevState) => {
            return {
              ...prevState,
              manualFuelVolume: {
                ...prevState.manualFuelVolume,
                unit: unit,
              },
            };
          });
        }
      }
    }
    form.setFields([
      {
        name: ["density", "inputBy"],
        errors: [],
      },
      {
        name: ["density", "tagPI", "tagPI"],
        errors: [],
      },
      {
        name: ["density", "tagPI", "unit"],
        errors: [],
      },
    ]);
    combustionData.volume.tagPI.forEach((_, index) => {
      form.setFields([
        {
          name: ["volume", "tagPI", index, "tagPI"],
          errors: [],
        },
        {
          name: ["volume", "tagPI", index, "unit"],
          errors: [],
        },
      ]);
    });
  }, [combustionData.volume.inputBy]);

  useEffect(() => {
    combustionData.volume.tagPI.forEach((el, index) => {
      const dupTagPI = combustionData.volume.tagPI.filter((item) => item.tagPI === el.tagPI);
      if (dupTagPI && dupTagPI.length > 1) {
        form.setFields([
          {
            name: ["volume", "tagPI", index, "tagPI"],
            errors: ["Duplicate Tag PI"],
          },
          {
            name: ["volume", "tagPI", index, "unit"],
            errors: [""],
          },
        ]);
      } else {
        form.setFields([
          {
            name: ["volume", "tagPI", index, "tagPI"],
            errors: [],
          },
          {
            name: ["volume", "tagPI", index, "unit"],
            errors: [],
          },
        ]);
      }
    });
  }, [combustionData.volume.tagPI]);

  return (
    <>
      {open && (
        <Modal
          title={<ModalTitle title="Fuel Volume" />}
          open={open}
          width={900}
          okText="Save"
          onCancel={onCancelFuelVolume}
          centered
          bodyStyle={{ height: 500, overflowY: "auto", overflowX: "hidden" }}
          maskClosable={false}
          footer={
            <Row className="w-full border-t" justify={"end"}>
              <Button className="mt-4" key="submit" type="primary" onClick={handleSaveCombustion} loading={loading}>
                Save
              </Button>
            </Row>
          }
        >
          <Form size="middle" layout="vertical" autoComplete="off" form={form} requiredMark={false} onValuesChange={handleChangeForm}>
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12} lg={12}>
                <Form.Item label={<FormLabel label={"Stack ID"} required />} name="stackId">
                  <Input disabled value={combustionData?.stackId} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={12}>
                <Form.Item label={<FormLabel label={"Source ID"} required />} name="sourceId">
                  <Input disabled value={combustionData?.sourceId} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12} lg={12}>
                <Form.Item label={<FormLabel label={"Area"} required />} name="area" required>
                  <Input disabled value={combustionData?.area} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={12}>
                <Form.Item label={<FormLabel label={"Fuel Name"} required />} name="fuelName" required>
                  <Input disabled value={combustionData?.fuelName} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12} lg={12}>
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <span style={{ fontWeight: "bold" }}>Volume</span>
                </div>
                <Form.Item label={<FormLabel label={"Input By"} required />} name="InputByVolume">
                  <Form.Item name={["volume", "inputBy"]} rules={validateRequired}>
                    <Select onChange={(value) => handleChangeInputBy("volume", value)} options={inputVolumeOptions} />
                  </Form.Item>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={12}>
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <span style={{ fontWeight: "bold" }}>Density</span>
                </div>
                <Form.Item label="Input By" name="inputByDensity">
                  <Form.Item name={["density", "inputBy"]} rules={validateInputBy}>
                    <Select
                      disabled={isManualDisabled}
                      onChange={(value) => handleChangeInputBy("density", value)}
                      options={inputDensityOptions}
                    />
                  </Form.Item>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12} lg={12}>
                <Form.Item label="Tag PI" name="tagPIVolume" style={{ marginBottom: "1px" }}>
                  {
                    <>
                      {combustionData.volume.tagPI.map((_, index) => (
                        <Space.Compact key={index} style={{ width: "100%" }}>
                          <Form.Item name={["volume", "tagPI", index, "tagPI"]} style={{ width: "70%" }} rules={validateVolumeTagPI}>
                            <Input disabled={isManualDisabled} onChange={(e) => handleChangeVolume("tagPI", e.target.value, index)} />
                          </Form.Item>
                          <Form.Item name={["volume", "tagPI", index, "unit"]} style={{ width: "20%" }} rules={validateInputByUnit}>
                            <Select
                              disabled={isManualDisabled}
                              options={unitVolumeOptions}
                              onChange={(value) => handleChangeVolume("unit", value, index)}
                            />
                          </Form.Item>
                          <Form.Item style={{ width: "10%", display: "flex", flexDirection: "row-reverse", justifyContent: "center" }}>
                            <div style={{ width: "10%" }}>
                              {!isManualDisabled && (
                                <Button type="text" icon={<DeleteOutlined />} onClick={() => handleDeleteVolume(index)} />
                              )}
                            </div>
                          </Form.Item>
                        </Space.Compact>
                      ))}
                    </>
                  }
                </Form.Item>
                {!isManualDisabled && (
                  <Space style={{ width: "100%", justifyContent: "right", padding: 5 }}>
                    <Button type="primary" icon={<PlusCircleOutlined />} onClick={addVolumeTagPI} disabled={isManualDisabled}></Button>
                  </Space>
                )}
              </Col>
              <Col xs={24} sm={12} lg={12}>
                <Form.Item label="Density" name="tagPIDensity">
                  <Space.Compact style={{ width: "100%" }}>
                    <Form.Item name={["density", "tagPI", "tagPI"]} style={{ width: "80%" }} rules={validateInputBy}>
                      {combustionData.density.inputBy === InputByEnum.MANUAL ? (
                        <InputNumber
                          style={{ width: "100%" }}
                          onKeyDown={(e) => {
                            if (combustionData.density.inputBy === InputByEnum.MANUAL) {
                              onKeyDownNumberWithDecimal(e);
                            }
                          }}
                          formatter={(value) => formatNumber(Number(value))}
                          onChange={(value) => handleChangeDensity("tagPI", String(value))}
                          disabled={isManualDisabled || isM3hrSelected}
                        />
                      ) : (
                        <Input
                          onKeyDown={(e) => {
                            if (combustionData.density.inputBy === InputByEnum.MANUAL) {
                              onKeyDownNumberWithDecimal(e);
                            }
                          }}
                          onChange={(e) => handleChangeDensity("tagPI", e.target.value)}
                          disabled={isManualDisabled || isM3hrSelected}
                        />
                      )}
                    </Form.Item>
                    <Form.Item name={["density", "tagPI", "unit"]} style={{ width: "20%" }} rules={validateInputByUnit}>
                      <Select
                        options={unitDensityTagPI}
                        onChange={(value) => handleChangeDensity("unit", value)}
                        disabled={isManualDisabled || isM3hrSelected}
                      />
                    </Form.Item>
                  </Space.Compact>
                </Form.Item>

                <Col span={24} className={"date-picker-group"}>
                  <CustomFormDatePicker
                    disabled={isManualDisabled}
                    name={"exceptionDateArr"}
                    label={"Exception Date"}
                    customClassName={"exception-date-period"}
                  />
                </Col>
              </Col>
            </Row>
          </Form>
        </Modal>
      )}
    </>
  );
};

export default FuelVolumeModal;
